import { makeStyles } from "@fluentui/react-components";
import { primer } from "./primerDarkTheme";

export const useRunHistoryChartStyles = makeStyles({
    card: {
        backgroundColor: primer.surface,
        borderRadius: "6px",
        border: `1px solid ${primer.border}`,
        flexShrink: 0,
        overflow: "clip",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
    },
    headerDivider: {
        height: "1px",
        width: "100%",
        backgroundColor: primer.border,
    },
    heading: { fontSize: "16px", fontWeight: 600, color: primer.fg },
    headerRight: { display: "flex", alignItems: "center", gap: "10px" },
    refreshBtn: {
        display: "flex",
        alignItems: "center",
        gap: "5px",
        border: `1px solid ${primer.border}`,
        background: "none",
        color: primer.fg,
        cursor: "pointer",
        padding: "3px 10px",
        borderRadius: "6px",
        fontSize: "12px",
        fontWeight: 500,
        ":hover": { backgroundColor: primer.overlay },
        ":disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    spinning: {
        animationName: {
            from: { transform: "rotate(0deg)" },
            to: { transform: "rotate(360deg)" },
        },
        animationDuration: "0.8s",
        animationTimingFunction: "linear",
        animationIterationCount: "infinite",
    },
    loadingMore: { display: "flex", alignItems: "center", gap: "3px" },
    loadingDot: {
        width: "5px",
        height: "5px",
        borderRadius: "50%",
        backgroundColor: primer.fgSubtle,
        animationName: {
            "0%, 80%, 100%": { opacity: 0.2, transform: "scale(0.8)" },
            "40%": { opacity: 1, transform: "scale(1)" },
        },
        animationDuration: "1.2s",
        animationIterationCount: "infinite",
        animationTimingFunction: "ease-in-out",
        ":nth-child(1)": { animationDelay: "0s" },
        ":nth-child(2)": { animationDelay: "0.2s" },
        ":nth-child(3)": { animationDelay: "0.4s" },
    },
    skeletonBar: {
        width: "40px",
        borderRadius: "3px 3px 0 0",
        backgroundColor: primer.overlay,
        animationName: {
            "0%, 100%": { opacity: 0.4 },
            "50%": { opacity: 0.8 },
        },
        animationDuration: "1.4s",
        animationIterationCount: "infinite",
        animationTimingFunction: "ease-in-out",
    },
    timeRangeGroup: {
        display: "flex",
        borderRadius: "6px",
        border: `1px solid ${primer.border}`,
        overflow: "hidden",
    },
    timeRangeBtn: {
        fontSize: "12px",
        color: primer.fgMuted,
        backgroundColor: primer.surface,
        border: "none",
        borderLeft: `1px solid ${primer.border}`,
        padding: "4px 10px",
        cursor: "pointer",
        outline: "none",
        transition: "background 0.1s, color 0.1s",
        "&:first-child": { borderLeft: "none" },
    },
    timeRangeBtnActive: {
        fontSize: "12px",
        color: primer.fg,
        backgroundColor: primer.overlay,
        border: "none",
        borderLeft: `1px solid ${primer.border}`,
        padding: "4px 10px",
        cursor: "pointer",
        outline: "none",
        fontWeight: 600,
        "&:first-child": { borderLeft: "none" },
    },
    legend: {
        display: "flex",
        gap: "16px",
        padding: "4px 16px 10px",
        alignItems: "center",
    },
    legendItem: {
        display: "flex",
        alignItems: "center",
        gap: "5px",
        fontSize: "11px",
        color: primer.fgMuted,
    },
    swatchPassed: {
        width: "7px", height: "7px", borderRadius: "50%", backgroundColor: primer.green,
    },
    swatchFailed: {
        width: "7px", height: "7px", borderRadius: "50%", backgroundColor: primer.orangeText,
    },
    swatchSkipped: {
        width: "7px", height: "7px", borderRadius: "50%", backgroundColor: primer.red,
    },
    swatchFlaky: {
        width: "7px", height: "7px", borderRadius: "50%", backgroundColor: primer.flakyYellow,
    },
    chartWrapper: {
        position: "relative",
        overflow: "hidden",
        marginTop: "4px",
    },
    emptyState: {
        height: "200px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "13px",
        color: primer.fgSubtle,
        fontStyle: "italic" as const,
    },
    shimmerSweep: {
        position: "absolute",
        top: "0",
        left: "-60%",
        width: "60%",
        height: "100%",
        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent)",
        animationName: {
            "0%": { transform: "translateX(0%)" },
            "100%": { transform: "translateX(400%)" },
        },
        animationDuration: "1.4s",
        animationIterationCount: "infinite",
        animationTimingFunction: "linear",
        pointerEvents: "none",
        zIndex: 5,
    },
    chartScroll: {
        overflow: "hidden",
    },
    chartBody: {
        padding: "24px 16px 16px",
        display: "flex",
        alignItems: "flex-end",
        gap: "3px",
        height: "180px",
        width: "100%",
    },
    barGroup: {
        flex: "1 1 0",
        minWidth: 0,
        maxWidth: "200px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "4px",
        cursor: "pointer",
        position: "relative",
    },
    barStack: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        borderRadius: "3px 3px 0 0",
        overflow: "hidden",
    },
    barPassed: { backgroundColor: primer.green, flexShrink: 0 },
    barFailed: { backgroundColor: primer.orangeText, flexShrink: 0 },
    barSkipped: { backgroundColor: primer.red, flexShrink: 0 },
    barFlaky: { backgroundColor: primer.flakyYellow, flexShrink: 0 },
    barLabel: { fontSize: "10px", color: primer.fgSubtle, marginTop: "4px", transform: "rotate(-45deg)", transformOrigin: "top right", whiteSpace: "nowrap", textAlign: "right" },
    barLabelHidden: { fontSize: "10px", color: primer.fgSubtle, marginTop: "4px", opacity: 0 },
    barEmpty: {
        width: "40px",
        height: "6px",
        borderRadius: "3px",
        backgroundColor: "rgba(255,255,255,0.1)",
    },
    loadingOverlay: {
        position: "absolute",
        top: "8px",
        left: "50%",
        transform: "translateX(-50%)",
        backgroundColor: "rgba(22, 27, 34, 0.9)",
        border: `1px solid ${primer.border}`,
        borderRadius: "6px",
        padding: "6px 16px",
        fontSize: "12px",
        color: primer.fgSubtle,
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    tooltipDate: { fontWeight: 600 },
    selected: {
        outline: `2px solid ${primer.blue}`,
        outlineOffset: "-2px",
        borderRadius: "4px",
    },
    tooltip: {
        position: "fixed",
        transform: "translate(-50%, -100%)",
        backgroundColor: primer.overlay,
        border: `1px solid ${primer.border}`,
        borderRadius: "6px",
        padding: "8px 10px",
        fontSize: "11px",
        color: primer.fg,
        whiteSpace: "nowrap" as const,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        pointerEvents: "none",
    },
    tooltipGreen: { color: primer.green },
    tooltipRed: { color: primer.red },
    tooltipYellow: { color: primer.orange },
    tooltipFlaky: { color: primer.flakyYellow },
    tooltipMuted: { color: primer.fgSubtle },
});
